import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Form,
  LoadingSpinner,
  ScrollArea,
} from '@ais-ui/ui-core';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { useDrawFromDialog, useDrawingContext } from '@ais-ui/react-openlayers';
import { GeoJson } from '@ais-ui/api-spec/shapes';
import { useLocalization } from '@ais-ui/locale-management';
import { useGetShareGroups } from '@ais-ui/websockets';
import { cn } from '@ais-ui/utils';
import { MutateBaseForm } from '../Mutations/MutateBaseForm';

import { DetectorFormTypes } from '../Mutations/detectorForm';
import { CableDetectorFormValues, fieldToCableAccordionMap } from './cableForm';
import { CableAdvancedForm, CableFields } from './CableForm';
import { useErrorNavigation } from '../detectorUtils';

interface DetectorDialogProps {
  form: UseFormReturn<CableDetectorFormValues>;
  onSubmit: (data: CableDetectorFormValues) => void;
  setShowOuterDialog: (showOuterDialog: boolean) => void;
  mutateType: 'edit' | 'create';
  isSubmitting: boolean;
}

export const CableDetectorDialog = ({
  form,
  onSubmit,
  setShowOuterDialog,
  mutateType,
  isSubmitting,
}: DetectorDialogProps) => {
  const { messages } = useLocalization();
  const [showDialog, setShowDialog] = useState<boolean>(true);
  const [showShape, setShowShape] = useState<GeoJson | null>(null);
  const { showDrawPanel } = useDrawingContext();
  const { onStartDrawingFromDialog } = useDrawFromDialog(form, setShowDialog);
  const { data: groups, isLoading: isLoadingGroups } = useGetShareGroups();
  const detectorType = 'cable';
  const onShowShape = useCallback((shape: GeoJson | undefined) => {
    if (!shape) {
      return;
    }
    setShowDialog(false);
    setShowShape(shape as GeoJson);
  }, []);

  const handleOpenChange = (isOpen: boolean) => {
    setShowDialog(isOpen); // Update showDialog state

    // Make sure that we are not unmounting the component when drawing, but we will unmount it if we are not drawing
    if (!showDrawPanel) {
      setShowOuterDialog(isOpen);
    }
  };

  const { openSection, setOpenSection } = useErrorNavigation(
    form,
    fieldToCableAccordionMap
  );

  const dialogTitle = useMemo(
    () =>
      mutateType === 'create'
        ? messages.eventdetectorsCreateTitle.replace('{label}', detectorType)
        : messages.eventdetectorsEditTitle.replace('{label}', detectorType),
    [mutateType, messages, detectorType]
  );
  return (
    <Form {...form}>
      <Dialog open={showDialog} onOpenChange={handleOpenChange}>
        <DialogContent className="pt-12 pl-12 pr-8 max-w-3xl lg:h-[760px] 2xl:h-[960px] h-[600px]">
          <DialogHeader className="h-0 space-y-3">
            <DialogTitle>{dialogTitle}</DialogTitle>
            <DialogDescription>
              {messages.eventdetectorDialogDescription}
            </DialogDescription>
          </DialogHeader>
          {isLoadingGroups && (
            <div className="h-[350px]">
              <LoadingSpinner></LoadingSpinner>
            </div>
          )}

          {!isLoadingGroups && !!groups && (
            <div className={cn('lg:h-[460px] 2xl:h-[670px] h-[300px]')}>
              <Accordion
                type="single"
                collapsible
                defaultValue="detector-item"
                value={openSection}
                onValueChange={setOpenSection}
              >
                <AccordionItem value="detector-item">
                  <AccordionTrigger>
                    <h3 className="text-lg font-medium">Detector</h3>
                  </AccordionTrigger>
                  <AccordionContent>
                    <ScrollArea
                      className="lg:h-[340px] 2xl:h-[530px] h-[250px] w-full py-2 pl-2 pr-4 overflow-hidden"
                      observeResize={true}
                    >
                      <MutateBaseForm
                        form={form as UseFormReturn<DetectorFormTypes>}
                        detectorType={detectorType}
                        onStartDrawing={onStartDrawingFromDialog}
                        onShowShape={onShowShape}
                      ></MutateBaseForm>
                    </ScrollArea>
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="advanced-item">
                  <AccordionTrigger>
                    <h3 className="text-lg font-medium">Advanced</h3>
                  </AccordionTrigger>
                  <AccordionContent>
                    <ScrollArea
                      className="lg:h-[340px] 2xl:h-[530px] h-[250px] w-full py-2 pl-2 pr-4 overflow-hidden"
                      observeResize={true}
                    >
                      <CableAdvancedForm form={form}></CableAdvancedForm>
                    </ScrollArea>
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="cable-item">
                  <AccordionTrigger>
                    <h3 className="text-lg font-medium">Cable triggers</h3>
                  </AccordionTrigger>
                  <AccordionContent>
                    <ScrollArea
                      className="lg:h-[340px] 2xl:h-[530px] h-[250px] w-full py-2 pl-2 pr-4 overflow-hidden"
                      observeResize={true}
                    >
                      <CableFields form={form}></CableFields>
                    </ScrollArea>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>
          )}

          <DialogFooter className="h-[0px] pr-4">
            <Button
              className="mt-4"
              id="submit-cable-event-detector-button"
              disabled={isSubmitting}
              onClick={form.handleSubmit(onSubmit)}
              type="submit"
            >
              {isSubmitting
                ? messages.genericSubmitting
                : messages.genericSubmit}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Form>
  );
};
